import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Token storage: sessionStorage (per-tab) instead of localStorage so each
// browser tab/window represents a separate session. Combined with the
// backend's per-role single-session enforcement on Core, a fresh login on
// a new tab/window kicks out the previous session automatically.
const TOKEN_KEY = "cpi_session_token";
const LAST_ACTIVITY_KEY = "cpi_last_activity";
const IS_MASTER_KEY = "cpi_is_master";
const IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours

const tokenStore = () => {
  if (typeof window !== "undefined" && window.sessionStorage) return window.sessionStorage;
  return { getItem: () => null, setItem: () => {}, removeItem: () => {} };
};
export const getToken = () => tokenStore().getItem(TOKEN_KEY);
const setToken = (t) => tokenStore().setItem(TOKEN_KEY, t);
const clearToken = () => {
  tokenStore().removeItem(TOKEN_KEY);
  tokenStore().removeItem(LAST_ACTIVITY_KEY);
  tokenStore().removeItem(IS_MASTER_KEY);
};
// Cached "is_master" flag for synchronous reads on first paint — eliminates
// the brief flash on the Hub where master-only decorations appear after the
// async /auth/session check resolves.
export const getCachedIsMaster = () => tokenStore().getItem(IS_MASTER_KEY) === "1";
const setCachedIsMaster = (val) => {
  if (val) tokenStore().setItem(IS_MASTER_KEY, "1");
  else tokenStore().removeItem(IS_MASTER_KEY);
};

// Axios interceptor: attach token to every request
axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: auto-logout on 401 / session invalidation.
// Important: the /auth/session diagnostic endpoint can 401 for transient
// reasons (replica lag on Core's single-session lookup, network blips). We do
// NOT clear the local token on those — only on protected resources.
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    const code = err.response?.data?.code;
    const url = err.config?.url || "";
    const isSessionDiag = url.endsWith("/auth/session");
    if (status === 401 && getToken() && !isSessionDiag) {
      // Token rejected on a real protected call — could be expiry or
      // single-session invalidation (Core).
      clearToken();
      if (code === "session_invalidated") {
        if (window.location.pathname !== "/") {
          window.location.href = "/?reason=session_invalidated";
        }
      }
    }
    return Promise.reject(err);
  }
);

// Track user activity — auto-logout after IDLE_TIMEOUT_MS of inactivity
const recordActivity = () => {
  if (getToken()) {
    tokenStore().setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  }
};

const checkIdleTimeout = () => {
  const last = parseInt(tokenStore().getItem(LAST_ACTIVITY_KEY) || "0", 10);
  if (!last) return;
  if (Date.now() - last > IDLE_TIMEOUT_MS) {
    clearToken();
    if (window.location.pathname !== "/" && !window.location.pathname.includes("-login")) {
      window.location.href = "/?reason=idle";
    }
  }
};

// Hook up listeners once
if (typeof window !== "undefined" && !window.__cpiIdleTracker) {
  window.__cpiIdleTracker = true;
  ["mousedown", "keydown", "scroll", "touchstart"].forEach(evt =>
    window.addEventListener(evt, recordActivity, { passive: true })
  );
  // Periodic check
  setInterval(checkIdleTimeout, 60 * 1000); // every minute
}

export const checkSession = async () => {
  try {
    const res = await axios.get(`${API}/auth/session`);
    // Update the cached master flag so subsequent first paints are correct.
    setCachedIsMaster(!!res.data?.is_master && !!res.data?.authenticated);
    return res.data;
  } catch {
    return { authenticated: false, roles: [], is_master: false };
  }
};

export const isRoleAuthenticated = async (role) => {
  const session = await checkSession();
  if (!session.authenticated) return false;
  if (session.is_master) return true;
  return session.roles.includes(role);
};

/**
 * Login with credentials.
 * Returns the full response — may include `requires_2fa`, `force_password_change`, or `token`.
 * Caller is responsible for handling 2FA / password-change flows.
 */
export const login = async (username, password, role) => {
  const res = await axios.post(`${API}/auth/validate`, { username, password, role });
  if (res.data.token) {
    setToken(res.data.token);
    setCachedIsMaster(!!res.data.is_master);
    tokenStore().setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  }
  return res.data;
};

/** Submit OTP for a 2FA-protected login. Returns token payload on success. */
export const verifyOTP = async (role, otp) => {
  const res = await axios.post(`${API}/auth/2fa/verify`, { role, otp });
  if (res.data.token) {
    setToken(res.data.token);
    setCachedIsMaster(!!res.data.is_master);
    tokenStore().setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  }
  return res.data;
};

/** Change password for a role. */
export const changePassword = async (role, currentPassword, newPassword) => {
  const res = await axios.post(`${API}/auth/change-password`, {
    role, current_password: currentPassword, new_password: newPassword,
  });
  return res.data;
};

export const logout = async () => {
  clearToken();
  try {
    await axios.post(`${API}/auth/logout`);
  } catch (err) {
    console.error("Logout cleanup failed:", err);
  }
};
